import { Component, OnInit } from '@angular/core';
import { VenueQuoteService } from '../shared/venue-quote.service';
import { VenueQuoteModel } from '../shared/venue-quote.model';
import { Column } from '@app/partials/datatable/data-table/column';
import { DataTableComponent } from '@app/partials/datatable/data-table/data-table.component';
import { PagedEntities } from '@app/shared/abstract/paged-entities';
import { RouteParams } from '@app/shared/abstract/route-params';
import { Destroyer } from '@app/shared/abstract/destroyer';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { LucideAngularModule, Eye, Download } from 'lucide-angular';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { FeatureToggleService } from '@app/feature-toggles/feature-toggle.service';
import { NotificationService } from '@app/shared/notification/notification.service';

@Component({
  selector: 'app-quotes-index',
  standalone: true,
  imports: [
    DataTableComponent,
    LucideAngularModule,
    TextInputComponent,
    ButtonComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './quotes-index.component.html',
  providers: [CurrencyPipe, DatePipe],
})
export class QuotesIndexComponent extends Destroyer implements OnInit {
  protected readonly ViewIcon = Eye;
  protected readonly DownloadIcon = Download;

  // TVG-139: CSV export, gated behind the "quote-export" feature toggle.
  protected exportEnabled = false;
  protected exporting = false;

  protected quotesPage?: PagedEntities<VenueQuoteModel>;
  protected routeParams = new RouteParams({
    pageNum: 1,
    pageSize: 25,
  });
  protected searchControl = new FormControl('', { nonNullable: true });

  tableColumns: Column<VenueQuoteModel>[] = [
    { field: 'id', header: '#', sortable: true, width: '8%' },
    {
      field: 'contact_name',
      header: 'Contact',
      sortable: true,
      callback: (quote: VenueQuoteModel) => quote.contact_name || '—',
    },
    {
      field: 'contact_email',
      header: 'Email',
      sortable: true,
      callback: (quote: VenueQuoteModel) => quote.contact_email || '—',
    },
    {
      field: 'status',
      header: 'Status',
      sortable: true,
      callback: (quote: VenueQuoteModel) => this.formatStatus(quote.status),
    },
    {
      field: 'venue_id',
      header: 'Venue',
      sortSource: 'venue_id',
      callback: (quote: VenueQuoteModel) => quote.venue?.name || '—',
    },
    {
      field: 'preferred_date',
      header: 'Preferred Date',
      type: 'date',
      sortable: true,
      callback: (quote: VenueQuoteModel) => {
        if (quote.status === 'draft') {
          const calculatedDate = quote.draft_default_date || quote.recommended_date;
          if (calculatedDate) {
            return `${this.datePipe.transform(calculatedDate, 'd MMM yyyy') ?? calculatedDate} (calculated)`;
          }

          return 'Calculated draft';
        }

        return quote.preferred_date ? this.datePipe.transform(quote.preferred_date, 'd MMM yyyy') ?? '' : '—';
      },
    },
    {
      field: 'preferred_total',
      header: 'Estimated Total',
      type: 'number',
      sortable: true,
      align: 'right',
      callback: (quote: VenueQuoteModel) => {
        if (quote.estimated_total_label) {
          return quote.estimated_total_label;
        }

        if (quote.preferred_total !== null && quote.preferred_total !== undefined) {
          return this.currencyPipe.transform(quote.preferred_total, 'GBP', 'symbol', '1.0-0') ?? '';
        }

        return '—';
      },
    },
    {
      field: 'created_at',
      header: 'Submitted',
      sortable: true,
      callback: (quote: VenueQuoteModel) =>
        quote.created_at ? this.datePipe.transform(quote.created_at, 'd MMM yyyy, HH:mm') ?? '' : '—',
    },
  ];

  rowActions = [
    {
      icon: this.ViewIcon,
      routerLink: (item: VenueQuoteModel) => ['/quotes', item.id],
    },
  ];

  constructor(
    private service: VenueQuoteService,
    private currencyPipe: CurrencyPipe,
    private datePipe: DatePipe,
    private featureToggleService: FeatureToggleService,
    private notificationService: NotificationService,
  ) {
    super();
  }

  private formatStatus(status?: string | null): string {
    if (!status) {
      return '—';
    }

    const normalized = status.replace(/_/g, ' ').trim();
    if (normalized === '') {
      return '—';
    }

    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }

  ngOnInit(): void {
    this.exportEnabled = this.featureToggleService.isEnabled('quote-export');
    this.loadData();

    this.searchControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        this.routeParams.filter = term ? [{ field: 'search', value: term }] : [];
        this.routeParams.pageNum = 1;
        this.loadData();
      });
  }

  loadData() {
    this.service.getPage(this.routeParams).subscribe((res) => {
      this.quotesPage = res;
    });
  }

  handleRoute(params: RouteParams) {
    this.routeParams.sort = params.sort;
    this.routeParams.pageNum = params.pageNum;
    this.routeParams.pageSize = params.pageSize;
    this.loadData();
  }

  exportCsv() {
    if (this.exporting) {
      return;
    }

    this.exporting = true;
    this.service.exportCsv().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `quotes-export-${this.datePipe.transform(new Date(), 'yyyy-MM-dd')}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.exporting = false;
      },
      error: () => {
        this.notificationService.error('Error exporting quotes');
        this.exporting = false;
      },
    });
  }
}
